import React, { useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { EVENT_CONFIG, STATE_COLORS, formatTs, formatTsFull, extractDischargeWatts, deriveStatePeriods } from './constants'

const useStyles = makeStyles(() => ({
  dlmTimelineWrapper: {
    margin: '12px 24px 0',
    padding: '16px 20px',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  dlmTimelineHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
    flexWrap: 'wrap',
    '& h3': {
      fontSize: '13px',
      fontWeight: 600,
      color: '#111827',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      margin: 0,
    },
  },
  dlmNoEvents: {
    fontSize: '12px',
    color: '#9ca3af',
    fontStyle: 'italic',
  },
  dlmTimelineLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  dlmLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: '#6b7280',
  },
  dlmLegendSwatch: {
    display: 'inline-block',
    width: '14px',
    height: '10px',
    borderRadius: '2px',
    border: '1px solid',
  },
  dlmLegendDivider: {
    width: '1px',
    height: '14px',
    background: '#d1d5db',
  },
  dlmTimelineBarContainer: {
    marginBottom: '12px',
  },
  dlmTimelineBar: {
    position: 'relative',
    height: '32px',
    background: '#f3f4f6',
    borderRadius: '4px',
    overflow: 'visible',
  },
  dlmStateBand: {
    position: 'absolute',
    top: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '2px',
  },
  dlmBandLabel: {
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    opacity: 0.7,
    pointerEvents: 'none',
  },
  dlmEventMarker: {
    position: 'absolute',
    top: '-2px',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    zIndex: 2,
    transition: 'transform 0.15s',
    '&:hover': { transform: 'translateX(-50%) scale(1.3)' },
  },
  dlmEventMarkerActive: {
    transform: 'translateX(-50%) scale(1.3)',
  },
  dlmMarkerIcon: {
    fontSize: '14px',
    lineHeight: 1,
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))',
  },
  dlmMarkerWatts: {
    fontSize: '8px',
    fontWeight: 700,
    color: '#374151',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '3px',
    padding: '0 3px',
    marginTop: '1px',
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
  },
  dlmTimeAxis: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '10px',
    color: '#9ca3af',
    marginTop: '4px',
    padding: '0 2px',
  },
  dlmEventDetail: {
    padding: '10px 14px',
    marginBottom: '12px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderLeft: '3px solid',
    borderRadius: '6px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 24px',
  },
  dlmDetailRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    fontSize: '12px',
    color: '#374151',
  },
  dlmDetailLabel: {
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#9ca3af',
    fontWeight: 500,
    minWidth: '56px',
  },
  dlmDetailReason: {
    flexBasis: '100%',
  },
  dlmWattsBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    background: '#ecfdf5',
    color: '#059669',
    border: '1px solid #a7f3d0',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
  },
}))

export default function DlmTimeline({ auditLogs, timeWindowFrom, timeWindowTo }) {
  const classes = useStyles()
  const [expandedId, setExpandedId] = useState(null)

  const events = useMemo(() => {
    if (!auditLogs || auditLogs.length === 0) return []
    return [...auditLogs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
  }, [auditLogs])

  const periods = useMemo(
    () => deriveStatePeriods(events, timeWindowFrom, timeWindowTo),
    [events, timeWindowFrom, timeWindowTo]
  )

  const totalMs = timeWindowTo - timeWindowFrom

  if (!auditLogs || auditLogs.length === 0) {
    return (
      <div className={classes.dlmTimelineWrapper}>
        <div className={classes.dlmTimelineHeader}>
          <h3>DLM Events</h3>
          <span className={classes.dlmNoEvents}>No DLM events in this time window</span>
        </div>
      </div>
    )
  }

  const positionPercent = (timestamp) => {
    const ms = new Date(timestamp).getTime() - timeWindowFrom.getTime()
    return Math.max(0, Math.min(100, (ms / totalMs) * 100))
  }

  return (
    <div className={classes.dlmTimelineWrapper}>
      <div className={classes.dlmTimelineHeader}>
        <h3>DLM Events</h3>
        <div className={classes.dlmTimelineLegend}>
          {Object.entries(STATE_COLORS).map(([state, colors]) => (
            <span key={state} className={classes.dlmLegendItem}>
              <span className={classes.dlmLegendSwatch} style={{ background: colors.bg, borderColor: colors.border }} />
              {state}
            </span>
          ))}
          <span className={classes.dlmLegendDivider} />
          {Object.entries(EVENT_CONFIG).map(([type, cfg]) => (
            <span key={type} className={classes.dlmLegendItem}>
              <span style={{ color: cfg.color, fontSize: '12px' }}>{cfg.icon}</span>
              {cfg.label}
            </span>
          ))}
        </div>
      </div>

      <div className={classes.dlmTimelineBarContainer}>
        <div className={classes.dlmTimelineBar}>
          {periods.map((period, index) => {
            const left = positionPercent(period.from)
            const right = positionPercent(period.to)
            const width = right - left
            const stateColors = STATE_COLORS[period.state] || STATE_COLORS.WATCHING
            return (
              <div
                key={index}
                className={classes.dlmStateBand}
                style={{
                  left: `${left}%`,
                  width: `${Math.max(width, 0.5)}%`,
                  background: stateColors.bg,
                  borderLeft: index > 0 ? `2px solid ${stateColors.border}` : 'none',
                }}
                title={`${period.state}: ${formatTs(period.from)} - ${formatTs(period.to)}`}
              >
                {width > 8 && (
                  <span className={classes.dlmBandLabel} style={{ color: stateColors.text }}>
                    {period.state}
                  </span>
                )}
              </div>
            )
          })}
          {events.map((event, index) => {
            const cfg = EVENT_CONFIG[event.event_type] || EVENT_CONFIG.STATE_TRANSITION
            const left = positionPercent(event.timestamp)
            const watts = extractDischargeWatts(event)
            const eventId = event.id || index
            return (
              <div
                key={eventId}
                className={`${classes.dlmEventMarker} ${expandedId === eventId ? classes.dlmEventMarkerActive : ''}`}
                style={{ left: `${left}%` }}
                onClick={() => setExpandedId(expandedId === eventId ? null : eventId)}
                title={`${cfg.label} at ${formatTs(event.timestamp)}`}
              >
                <span className={classes.dlmMarkerIcon} style={{ color: cfg.color }}>{cfg.icon}</span>
                {watts != null && event.event_type !== 'STATE_TRANSITION' && (
                  <span className={classes.dlmMarkerWatts}>{watts}W</span>
                )}
              </div>
            )
          })}
        </div>
        <div className={classes.dlmTimeAxis}>
          <span>{formatTs(timeWindowFrom.toISOString())}</span>
          <span>{formatTs(timeWindowTo.toISOString())}</span>
        </div>
      </div>

      {expandedId != null && (() => {
        const event = events.find((e) => (e.id || events.indexOf(e)) === expandedId)
        if (!event) return null
        const cfg = EVENT_CONFIG[event.event_type] || EVENT_CONFIG.STATE_TRANSITION
        const watts = extractDischargeWatts(event)
        return (
          <div className={classes.dlmEventDetail} style={{ borderLeftColor: cfg.color }}>
            <div className={classes.dlmDetailRow}>
              <span className={classes.dlmDetailLabel}>Time</span>
              <span>{formatTsFull(event.timestamp)}</span>
            </div>
            <div className={classes.dlmDetailRow}>
              <span className={classes.dlmDetailLabel}>Event</span>
              <span style={{ color: cfg.color, fontWeight: 600 }}>{event.event_type}</span>
            </div>
            <div className={classes.dlmDetailRow}>
              <span className={classes.dlmDetailLabel}>State</span>
              <span>{event.from_state} {'\u2192'} {event.to_state}</span>
            </div>
            {watts != null && (
              <div className={classes.dlmDetailRow}>
                <span className={classes.dlmDetailLabel}>Discharge</span>
                <span className={classes.dlmWattsBadge}>{watts}W</span>
              </div>
            )}
            {event.reason && (
              <div className={`${classes.dlmDetailRow} ${classes.dlmDetailReason}`}>
                <span className={classes.dlmDetailLabel}>Reason</span>
                <span>{event.reason}</span>
              </div>
            )}
          </div>
        )
      })()}
    </div>
  )
}
